import { httpClient } from './http-client';
import { storageService } from '../../utils/storage';
import { suscripcionesService } from './suscripciones.service';
import { ApiError } from './api.types';
import type { ApiResponse } from './api.types';
import { HTTP_STATUS } from './api.config';
import type { LoginRequest, LoginResponse, RefreshTokenResponse, Usuario, RegisterRequest } from '../../modelos/Auth';

const autoSelectWorkspace = (loginResponse: LoginResponse): void => {
  const { empresas, establecimientos } = loginResponse;

  if (empresas.length === 0) return;

  const empresaToSelect = empresas.length === 1
    ? empresas[0]
    : empresas.find(e => e.esPrincipal);

  if (!empresaToSelect) return;

  const empresaEstabs = establecimientos.filter(e => e.empresaId === empresaToSelect.id);
  const estabPrincipal = empresaEstabs.find(e => e.esPrincipal);
  const estabAuto = estabPrincipal ?? (empresaEstabs.length === 1 ? empresaEstabs[0] : null);

  if (estabAuto !== null || empresaEstabs.length === 0) {
    storageService.setWorkspace(empresaToSelect.id, estabAuto?.id);
  }
};

const procesarLoginResponse = (responseData: LoginResponse, fallbackEmail: string, errorSiInvalido: string): LoginResponse => {
  if (!responseData.token || !responseData.id) {
    throw new Error(errorSiInvalido);
  }

  const loginResponse: LoginResponse = {
    token: responseData.token,
    refreshToken: responseData.refreshToken || '',
    expiresIn: responseData.expiresIn || 28800,
    id: responseData.id,
    nombre: responseData.nombre || '',
    apellido: responseData.apellido || '',
    email: responseData.email || fallbackEmail,
    requiereCambioPassword: responseData.requiereCambioPassword || false,
    esOnboarding: responseData.esOnboarding,
    esSuperAdminSenciyo: responseData.esSuperAdminSenciyo ?? false,
    esCreadoPorApiKey: responseData.esCreadoPorApiKey,
    esCuentaDemo: responseData.esCuentaDemo ?? false,
    empresas: Array.isArray(responseData.empresas) ? responseData.empresas : [],
    establecimientos: Array.isArray(responseData.establecimientos) ? responseData.establecimientos : [],
    requiereSeleccionContexto: responseData.requiereSeleccionContexto ?? (responseData.empresas?.length > 1),
    configuraciones: Array.isArray(responseData.configuraciones) ? responseData.configuraciones : [],
    permisos: Array.isArray(responseData.permisos) ? responseData.permisos : [],
    tipoCambio: responseData.tipoCambio ?? undefined,
    codigosDetraccion: Array.isArray(responseData.codigosDetraccion) ? responseData.codigosDetraccion : [],
    tiposOperacion: Array.isArray(responseData.tiposOperacion) ? responseData.tiposOperacion : [],
    suscripcion: responseData.suscripcion ?? null,
  };

  storageService.saveAuth(loginResponse);
  autoSelectWorkspace(loginResponse);
  if (loginResponse.tipoCambio) {
    storageService.saveTipoCambio(loginResponse.tipoCambio);
  }
  void suscripcionesService.cargarLote(loginResponse.empresas.map(e => e.id));

  return loginResponse;
};

export const authService = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const loginData: LoginRequest = { email, password };
    const response = await httpClient.post<LoginResponse>('/usuarios/login', loginData);

    if (!response.data) {
      throw new Error('No se recibió datos del servidor');
    }

    return procesarLoginResponse(response.data, email, 'Credenciales inválidas');
  },

  demoLogin: async (): Promise<LoginResponse> => {
    const response = await httpClient.post<LoginResponse>('/usuarios/demo-login', {});

    if (!response.data) {
      throw new Error('No se recibió datos del servidor');
    }

    return procesarLoginResponse(response.data, 'demo@senciyo.pe', 'La cuenta Demo no está disponible en este momento');
  },

  register: async (data: RegisterRequest): Promise<LoginResponse> => {
    const registerData = {
      ...data,
      requiereCambioPassword: data.requiereCambioPassword ?? false,
      esActivo: data.esActivo ?? true,
    };

    const response = await httpClient.post<LoginResponse>('/usuarios', registerData);

    if (!response.data) {
      throw new Error('No se recibió datos del servidor');
    }

    return procesarLoginResponse(response.data, data.email, 'Error al crear la cuenta');
  },

  logout: async (): Promise<void> => {
    try {
      await httpClient.post('/usuarios/logout');
    } finally {
      storageService.clearAll();
    }
  },

  refreshToken: async (): Promise<RefreshTokenResponse | null> => {
    const refreshToken = storageService.getRefreshToken();
    if (!refreshToken) return null;

    try {
      const response = await httpClient.post<RefreshTokenResponse>('/usuarios/refresh-token', { refreshToken });

      if (response.data) {
        storageService.updateToken(
          response.data.token,
          response.data.refreshToken,
          response.data.expiresIn
        );
      }

      return response.data;
    } catch (error) {
      if (error instanceof ApiError && error.status === HTTP_STATUS.UNAUTHORIZED) {
        storageService.clearAll();
      }
      return null;
    }
  },

  getCurrentUser: async (): Promise<ApiResponse<Usuario>> => {
    return httpClient.get<Usuario>('/usuarios/me');
  },

  checkEmailExists: async (email: string): Promise<boolean> => {
    const response = await httpClient.get<{ existe: boolean; ruc?: string | null }>(`/usuarios/existe-email?email=${encodeURIComponent(email)}`);
    return response.data?.existe ?? false;
  },

  requestPasswordReset: async (email: string): Promise<void> => {
    await httpClient.post('/usuarios/reset-password-request', { email });
  },

  confirmPasswordReset: async (token: string, nuevoPassword: string): Promise<void> => {
    await httpClient.post('/usuarios/reset-password-confirm', { token, nuevoPassword });
  },

  isAuthenticated: (): boolean => {
    return storageService.isAuthenticated();
  },

  getUser: (): Usuario | null => {
    return storageService.getUser();
  },

  getToken: (): string | null => {
    return storageService.getToken();
  },

  validateSession: async (): Promise<boolean> => {
    if (!storageService.isAuthenticated()) return false;

    if (storageService.isTokenExpired()) {
      const refreshed = await authService.refreshToken();
      return refreshed !== null;
    }

    return true;
  },
};
